const siteName = "Veronics";
const productionSiteUrl = "https://veronics.org";
export const openGraphImageUrl = `${productionSiteUrl}/veronics-social-card.png`;
const publicMarketingRoutePaths = new Set([
  "/",
  "/about-us",
  "/contact-us",
  "/features",
  "/forgot-password",
  "/login",
  "/sign-up",
  "/whats-new",
]);
export const formatDocumentTitle = (title: string, isProduction = import.meta.env.PROD) =>
  `${isProduction ? "" : "[Local] "}${title} | ${siteName}`;
export const getDocumentTitle = (title: string) => formatDocumentTitle(title);

type RouteTitleContract = {
  title: string;
  description?: string;
  /** A non-sensitive resource name returned by this route's existing loader. */
  dynamicField?: "name" | "roleName";
};

// This is deliberately keyed by generated router full paths. Keep exemptions narrow and
// reviewable: these entries are layouts/redirects, not pages a user reads.
export const routeTitleExemptions = new Set(["/_shell", "/about", "/site-admin"]);

export const routeTitleContracts: Record<string, RouteTitleContract> = {
  "/my/change-password": { title: "Change Password" },
  "/": {
    title: "Alternative Education Management Software",
    description:
      "Run enrollment, scheduling, family records, and tuition tracking for homeschool co-ops, microschools, hybrid programs, and alternative education communities in one place.",
  },
  "/about-us": {
    title: "About Veronics: Software for Small Learning Communities",
    description:
      "Veronics is operating software for homeschool co-ops, microschools, and small learning communities, supporting enrollment, schedules, records, and tuition.",
  },
  "/contact-us": {
    title: "Contact Veronics",
    description:
      "Talk with Veronics about enrollment, scheduling, tuition tracking, and operations for your learning community.",
  },
  "/features": {
    title: "Features for Homeschool Co-ops and Microschools",
    description:
      "Explore how Veronics helps learning communities manage applications, class enrollment, schedules, family records, tuition, and recorded payments.",
  },
  "/forgot-password": {
    title: "Reset Your Password",
    description: "Reset your Veronics password or create one from the email link we send you.",
  },
  "/login": {
    title: "Log In",
    description: "Sign in to Veronics with your email and password or request a magic link.",
  },
  "/sign-up": {
    title: "Sign Up",
    description: "Create your Veronics account to get started with enrollment, scheduling, and tuition tools.",
  },
  "/whats-new": {
    title: "What's New",
    description:
      "See recent Veronics product updates, from security improvements to email design and organization branding.",
  },
  "/account/auto-login": { title: "Signing You In" },
  "/account/reset-password": { title: "Reset Your Password" },
  "/account/confirm-email": { title: "Confirm Your Email" },
  "/account/confirm-child-email": { title: "Confirm Child Email" },
  "/account/two-factor-code": { title: "Two-Factor Verification" },
  "/account/two-factor-options": { title: "Two-Factor Authentication" },
  "/account/invitations/accept": { title: "Accept Invitation" },
  "/my/classes": { title: "My Classes" },
  "/my/classes/$classId": { title: "Class Details" },
  "/my/family": { title: "My Family" },
  "/my/invitations": { title: "My Invitations" },
  "/my/organizations": { title: "My Organizations" },
  "/my/profile": { title: "My Profile" },
  "/organizations": { title: "Organizations" },
  "/organizations/new": { title: "Create Organization" },
  "/organizations/$organizationId": { title: "Organization", dynamicField: "name" },
  "/site-admin/audit-logs": { title: "Audit Logs" },
  "/site-admin/contact-submissions": { title: "Contact Submissions" },
  "/site-admin/contact-submissions/$contactSubmissionId": { title: "Contact Submission" },
  "/site-admin/dashboard": { title: "Site Admin Dashboard" },
  "/site-admin/impersonation": { title: "Impersonation" },
  "/site-admin/invitation-diagnostics": { title: "Invitation Diagnostics" },
  "/site-admin/organizations": { title: "Organizations" },
  "/site-admin/overview": { title: "Site Admin Overview" },
  "/site-admin/settings": { title: "Site Settings" },
  "/site-admin/users": { title: "Users" },
  "/organizations/$organizationId/admin": { title: "Administration" },
  "/organizations/$organizationId/admin/admins": { title: "Administrators" },
  "/organizations/$organizationId/admin/all-members": { title: "All Members" },
  "/organizations/$organizationId/admin/applications": { title: "Applications" },
  "/organizations/$organizationId/admin/applications/$applicationId": { title: "Application" },
  "/organizations/$organizationId/admin/class-days": { title: "Class Days" },
  "/organizations/$organizationId/admin/class-days/$classDayId": { title: "Class Day" },
  "/organizations/$organizationId/admin/class-days/new": { title: "Create Class Day" },
  "/organizations/$organizationId/admin/enrollment-days": { title: "Enrollment Days" },
  "/organizations/$organizationId/admin/families": { title: "Families" },
  "/organizations/$organizationId/admin/families/$familyId": { title: "Family Details" },
  "/organizations/$organizationId/admin/family-balances": { title: "Family Balances" },
  "/organizations/$organizationId/admin/fee-collections": { title: "Fee Collections" },
  "/organizations/$organizationId/admin/invitations": { title: "Invitations" },
  "/organizations/$organizationId/admin/parents": { title: "Parents" },
  "/organizations/$organizationId/admin/forms": { title: "Forms" },
  "/organizations/$organizationId/admin/forms/$formId": { title: "Form Details", dynamicField: "name" },
  "/organizations/$organizationId/admin/forms/$formId/responses/$responseId": { title: "Submitted Form Response" },
  "/organizations/$organizationId/admin/forms/new": { title: "Create Form" },
  "/organizations/$organizationId/admin/payments": { title: "Payments" },
  "/organizations/$organizationId/admin/roles": { title: "Roles" },
  "/organizations/$organizationId/admin/roles/$roleId": { title: "Role Details", dynamicField: "roleName" },
  "/organizations/$organizationId/admin/schedule": { title: "Schedule" },
  "/organizations/$organizationId/admin/semesters": { title: "Semesters" },
  "/organizations/$organizationId/admin/semesters/$semesterId": { title: "Semester Details", dynamicField: "name" },
  "/organizations/$organizationId/admin/semesters/new": { title: "Create Semester" },
  "/organizations/$organizationId/admin/settings": { title: "Organization Settings" },
  "/organizations/$organizationId/admin/students": { title: "Students" },
  "/organizations/$organizationId/admin/teacher-payments": { title: "Teacher Payments" },
  "/organizations/$organizationId/admin/teachers": { title: "Teachers" },
  "/organizations/$organizationId/admin/teachers/new-anonymous": { title: "Create Teacher" },
  "/organizations/$organizationId/admin/classes": { title: "Classes" },
  "/organizations/$organizationId/admin/classes/$classId": { title: "Class Details", dynamicField: "name" },
  "/organizations/$organizationId/admin/classes/new": { title: "Create Class" },
  "/organizations/$organizationId/parent": { title: "Parent Portal" },
  "/organizations/$organizationId/parent/adults": { title: "Family Adults" },
  "/organizations/$organizationId/parent/classes": { title: "Classes" },
  "/organizations/$organizationId/parent/classes/$classId": { title: "Class Details" },
  "/organizations/$organizationId/parent/classes/$classId/updates/$updateId": { title: "Class Update" },
  "/organizations/$organizationId/parent/attachment-download/$classId/$updateId/$attachmentId": {
    title: "Attachment Download",
  },
  "/organizations/$organizationId/parent/enrollment": { title: "Enrollment" },
  "/organizations/$organizationId/parent/enrollment/$studentId/$classDayId": { title: "Enrollment" },
  "/organizations/$organizationId/parent/feed": { title: "Updates" },
  "/organizations/$organizationId/parent/help-center": { title: "Help Center" },
  "/organizations/$organizationId/parent/forms": { title: "Forms" },
  "/organizations/$organizationId/parent/forms/$formId": { title: "Form Details", dynamicField: "name" },
  "/organizations/$organizationId/parent/messages": { title: "Messages" },
  "/organizations/$organizationId/parent/payments": { title: "Payments" },
  "/organizations/$organizationId/parent/schedule": { title: "Schedule" },
  "/organizations/$organizationId/parent/students": { title: "Students" },
  "/organizations/$organizationId/teacher": { title: "Teacher Portal" },
  "/organizations/$organizationId/teacher/classes": { title: "Classes" },
  "/organizations/$organizationId/teacher/classes/$classId": { title: "Class Details" },
  "/organizations/$organizationId/teacher/classes/$classId/roster": { title: "Class Roster" },
  "/organizations/$organizationId/teacher/classes/$classId/updates": { title: "Class Updates" },
  "/organizations/$organizationId/teacher/classes/$classId/updates/new": { title: "Create Class Update" },
  "/organizations/$organizationId/teacher/classes/$classId/updates/$updateId": { title: "Class Update" },
};

const matchRoute = (pathname: string, routePath: string) => {
  const pathSegments = pathname.replace(/\/$/, "").split("/");
  const routeSegments = routePath.replace(/\/$/, "").split("/");

  return (
    pathSegments.length === routeSegments.length &&
    routeSegments.every((segment, index) => segment.startsWith("$") || segment === pathSegments[index])
  );
};

const safeLoadedName = (loaderData: unknown, field: NonNullable<RouteTitleContract["dynamicField"]>) => {
  if (!loaderData || typeof loaderData !== "object") {
    return undefined;
  }
  const name = (loaderData as Record<string, unknown>)[field];
  return typeof name === "string" && name.trim() ? name.trim() : undefined;
};

const getRouteTitleContract = (pathname: string) => {
  const routePath = Object.keys(routeTitleContracts).reduce<string | undefined>(
    (best, candidate) =>
      matchRoute(pathname, candidate) && (best === undefined || candidate.length > best.length) ? candidate : best,
    undefined,
  );

  return routePath ? routeTitleContracts[routePath] : undefined;
};

export const resolveRouteTitle = (pathname: string, loaderData?: unknown) => {
  const contract = getRouteTitleContract(pathname);
  if (!contract) {
    return getDocumentTitle("Page Not Found");
  }
  const dynamicName = contract.dynamicField ? safeLoadedName(loaderData, contract.dynamicField) : undefined;
  return getDocumentTitle(dynamicName ?? contract.title);
};

type TitleRouteMatch = {
  fullPath: string;
  loaderData?: unknown;
  status?: "pending" | "success" | "error" | "redirected" | "notFound";
  globalNotFound?: boolean;
};

/**
 * Resolves the title from TanStack Router's active match list. This lets the root
 * route supply declarative head metadata without each leaf route duplicating it.
 */
export const resolveRouteTitleFromMatches = (matches: readonly TitleRouteMatch[]) => {
  if (matches.some((match) => match.status === "error")) {
    return getDocumentTitle("Something Went Wrong");
  }
  if (matches.some((match) => match.status === "notFound" || match.globalNotFound)) {
    return getDocumentTitle("Page Not Found");
  }

  const match = [...matches].reverse().find(({ fullPath }) => fullPath in routeTitleContracts);
  return match ? resolveRouteTitle(match.fullPath, match.loaderData) : getDocumentTitle("Page Not Found");
};

export const resolveRouteHeadMetadata = (matches: readonly TitleRouteMatch[]) => {
  const title = resolveRouteTitleFromMatches(matches);
  const match = [...matches].reverse().find(({ fullPath }) => fullPath in routeTitleContracts);
  const contract = match ? getRouteTitleContract(match.fullPath) : undefined;

  const pathname = match?.fullPath;
  const isPublicMarketingPage = pathname !== undefined && publicMarketingRoutePaths.has(pathname);
  const canonicalPath = pathname === "/" ? "" : pathname;

  return {
    title,
    description: contract?.description,
    canonicalUrl: isPublicMarketingPage ? `${productionSiteUrl}${canonicalPath}` : undefined,
    openGraphImageUrl: isPublicMarketingPage ? openGraphImageUrl : undefined,
  };
};
